import { useState } from 'react';
import { Shield, MapPin, Camera, Phone, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { authAPI } from '@/lib/api';

export default function ConsentModal({ onAccepted }) {
  const [loading, setLoading] = useState(false);

  const handleAccept = async () => {
    setLoading(true);
    try {
      await authAPI.recordConsent(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.consent_given = true;
      localStorage.setItem('user', JSON.stringify(user));
      onAccepted();
    } catch {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[1010] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto animate-slide-up">
        <div className="bg-[#026536] px-6 pt-8 pb-6 sm:rounded-t-2xl rounded-t-2xl">
          <Shield className="text-white/80 mb-3" size={28} />
          <h2 className="text-lg font-extrabold text-white">Data Privacy Consent</h2>
          <p className="text-sm text-white/60 mt-1">UPASTITH by Uplift Health Hubs</p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <p data-testid="consent-intro" className="text-sm text-[#1a2529]/70 leading-relaxed">
            In accordance with the <strong>Digital Personal Data Protection Act, 2023 (DPDP Act)</strong>, we require your consent before collecting and processing your personal data. Please review the following:
          </p>

          <div className="space-y-3">
            <ConsentItem
              icon={MapPin}
              title="Location Data"
              desc="Your GPS coordinates are captured during check-in and check-out to verify your presence at the assigned work location."
            />
            <ConsentItem
              icon={Camera}
              title="Photographs"
              desc="Selfie photographs are captured during attendance marking for identity verification purposes."
            />
            <ConsentItem
              icon={Phone}
              title="Contact Information"
              desc="Your mobile number, emergency contact details, and address are stored for organizational and safety purposes."
            />
            <ConsentItem
              icon={Shield}
              title="Employment Records"
              desc="Attendance records, leave history, and work schedule data are maintained for HR and compliance requirements."
            />
          </div>

          <div className="bg-[#f4f4f5] rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-[#1a2529]/70">Your Rights (DPDP Act 2023):</p>
            <ul className="text-xs text-[#1a2529]/50 space-y-1.5 list-none">
              <li className="flex items-start gap-2"><CheckCircle2 size={12} className="text-[#026536] mt-0.5 shrink-0" /> Right to access your personal data</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={12} className="text-[#026536] mt-0.5 shrink-0" /> Right to correction of inaccurate data</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={12} className="text-[#026536] mt-0.5 shrink-0" /> Right to erasure of personal data</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={12} className="text-[#026536] mt-0.5 shrink-0" /> Right to withdraw consent at any time</li>
              <li className="flex items-start gap-2"><CheckCircle2 size={12} className="text-[#026536] mt-0.5 shrink-0" /> Right to grievance redressal</li>
            </ul>
          </div>

          <p className="text-[11px] text-[#1a2529]/40 leading-relaxed">
            Your data is encrypted at rest and processed solely for attendance management and organizational purposes. Data is retained as per the company's retention policy and deleted thereafter. Contact your administrator for data-related requests.
          </p>

          <div className="flex flex-col gap-2 pb-2">
            <Button
              data-testid="consent-accept-btn"
              onClick={handleAccept}
              disabled={loading}
              className="w-full h-12 bg-[#026536] hover:bg-[#026536]/90 text-white font-semibold rounded-xl"
            >
              {loading ? <Loader2 className="animate-spin mr-2" size={16} /> : <CheckCircle2 className="mr-2" size={16} />}
              I Accept & Give Consent
            </Button>
            <p className="text-[10px] text-center text-[#1a2529]/30">
              By accepting, you consent to the collection and processing of your data as described above.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ConsentItem({ icon: Icon, title, desc }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg bg-[#026536]/5">
      <Icon size={16} className="text-[#026536] mt-0.5 shrink-0" />
      <div>
        <p className="text-sm font-semibold text-[#1a2529]">{title}</p>
        <p className="text-xs text-[#1a2529]/50 mt-0.5 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}
